/**
 * Admin V3 — Operations / Notifications — Table component
 *
 * 7 columns: (expand toggle) | Date / Heure | Canal | Type | Destinataire
 *          | Réservation | Statut.
 *
 * Per-row expandable detail panel shows:
 *   - provider, provider_id
 *   - message_type
 *   - error_message (if status=failed)
 *
 * Réservation column: when booking_id present, render booking_reference as a
 * link to legacy /admin/bookings/:booking_id (Phase 2.2 precedent). Otherwise
 * show booking_reference as plain monospace text or em-dash.
 *
 * Failed rows (status=failed) get a rose left-border for visual scan.
 *
 * Resend: FAILED rows expose a resend control in the expanded panel (AV3 A-5),
 * RBAC-gated (admin:notifications:log) and F14-honesty-gated (rendered disabled
 * with an explicit reason until the backend resend actually re-delivers).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import NotificationResendControl from './NotificationResendControl';
import { Bell, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  EmptyState,
  SmallPill,
  notificationStatusMeta,
  notificationChannelMeta,
  notificationTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(7).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ log, locale }) => {
  const rows = [
    log.provider     && [t('operations.notifications.detail.provider',     locale), log.provider],
    log.provider_id  && [t('operations.notifications.detail.providerId',   locale), log.provider_id],
    log.message_type && [t('operations.notifications.detail.messageType',  locale), log.message_type],
    log.status === 'failed' && log.error_message
      && [t('operations.notifications.detail.errorMessage',  locale), log.error_message],
  ].filter(Boolean);

  if (rows.length === 0) {
    return (
      <div className="text-[11px] italic text-slate-400 px-2 py-1">—</div>
    );
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
            <dd className="text-slate-700 font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const OperationsNotificationsTable = ({ logs, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-operations-notifications-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('operations.notifications.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-operations-notifications-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('operations.notifications.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Bell}
      title={t('operations.notifications.empty.title', locale)}
      desc={t(isFilterActive ? 'operations.notifications.empty.descFilter' : 'operations.notifications.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('operations.notifications.col.datetime',  locale),
                t('operations.notifications.col.channel',   locale),
                t('operations.notifications.col.type',      locale),
                t('operations.notifications.col.recipient', locale),
                t('operations.notifications.col.booking',   locale),
                t('operations.notifications.col.status',    locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              logs.map((log) => {
                const key = log.id || `${log.timestamp || log.sent_at}-${log.recipient}`;
                const isExpanded = Boolean(expanded[key]);
                const channelKey = (log.channel || log.type || '').toLowerCase();
                const channelMeta = notificationChannelMeta[channelKey] || {
                  label: channelKey || '—',
                  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
                };
                const typeMeta = notificationTypeMeta[log.notification_type] || {
                  label: log.notification_type || log.message_type || '—',
                };
                const statusMeta = notificationStatusMeta[log.status] || {
                  label: log.status || '—',
                  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
                };
                const isFailed = log.status === 'failed';
                const ts = log.timestamp || log.sent_at;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isFailed && 'bg-rose-50/30'
                      )}
                      style={isFailed ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      {/* Expand */}
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded
                            ? t('operations.notifications.detail.hide', locale)
                            : t('operations.notifications.detail.show', locale)}
                          data-testid={`adminv3-operations-notifications-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Date / Heure */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(ts, locale)}
                      </td>

                      {/* Canal */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={channelMeta} />
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3.5 text-slate-700 text-[12px]">
                        {typeMeta.label}
                      </td>

                      {/* Destinataire */}
                      <td className="px-4 py-3.5 text-slate-700 font-mono text-[12px] break-all max-w-[240px]">
                        {log.recipient || '—'}
                      </td>

                      {/* Réservation */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {log.booking_reference && log.booking_id ? (
                          <Link
                            to={bookingDetailPath(log.booking_id)}
                            className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 hover:underline"
                            data-testid={`adminv3-operations-notifications-booking-${key}`}
                          >
                            {log.booking_reference}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : log.booking_reference ? (
                          <span className="text-slate-500">{log.booking_reference}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={7} className="px-4 py-2">
                          <DetailPanel log={log} locale={locale} />
                          {isFailed && (
                            <NotificationResendControl notification={log} onDone={onRetry} locale={locale} />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OperationsNotificationsTable;
